import Menu from './menu';

import { Opcodes, Packets } from '@kaetram/common/network';

import type Player from '../entity/character/player/player';
import type Task from '../entity/character/player/task';
import type Game from '../game';

type FlagSubmitCallback = (key: string, flag: string) => void;

export default class Quests extends Menu {
    // Contains the list of all the quests and their respective status.
    private list: HTMLUListElement = document.querySelector('#quests-container > ul')!;

    // Contains information about a selected quest.
    private title: HTMLElement = document.querySelector('#quest-log-title')!;
    private shortDescription: HTMLElement = document.querySelector('#quest-log-shortdesc')!;
    private description: HTMLElement = document.querySelector('#quest-log-description')!;
    private rewards: HTMLElement = document.querySelector('#quest-log-rewards')!;

    // Kill tracker elements
    private killTracker: HTMLElement = document.querySelector('#quest-kill-tracker')!;
    private killList: HTMLUListElement = document.querySelector('#quest-kill-list')!;

    // CTF flag submission elements
    private flagSection: HTMLElement = document.querySelector('#quest-flag-section')!;
    private flagInput: HTMLInputElement = document.querySelector('#quest-flag-input')!;
    private flagSubmitBtn: HTMLElement = document.querySelector('#quest-flag-submit')!;
    private flagMsg: HTMLElement = document.querySelector('#quest-flag-msg')!;

    private currentQuestKey = '';
    private flagSubmitCallback?: FlagSubmitCallback;

    public constructor(private player: Player) {
        super('#quests', '#close-quests', '#quests-button');

        this.flagSubmitBtn.addEventListener('click', () => this.submitFlag());
        this.flagInput.addEventListener('keydown', (e) => {
            e.stopPropagation(); // prevent game hotkeys (Enter opens chat, WASD moves player)
            if (e.key === 'Enter') this.submitFlag();
        });
    }

    private achievementElements = new Map<string, HTMLLIElement>();

    public handle(opcode: Opcodes.Quest, key = ''): void {
        switch (opcode) {
            case Opcodes.Quest.Batch: {
                for (let quest of Object.values(this.player.quests)) this.createElement(quest);
                break;
            }
            case Opcodes.Quest.Progress: {
                return this.handleProgress(key);
            }
        }

        this.buildLog(this.player.quests.tutorial);
    }

    /**
     * Called when the achievement batch arrives from the server. Adds all
     * currently active (started, not finished) achievements to the Quests panel.
     */

    public syncAchievements(): void {
        for (let key in this.player.achievements) this.syncAchievement(key);
    }

    /**
     * Adds, updates, or removes an achievement entry in the Quests panel based
     * on its current state. Only started-and-not-finished achievements appear.
     */

    public syncAchievement(key: string): void {
        let task = this.player.achievements[key];
        if (!task) return;

        let existing = this.achievementElements.get(key),
            shouldShow = task.isStarted() && !task.isFinished();

        if (!shouldShow) {
            if (existing) {
                existing.remove();
                this.achievementElements.delete(key);

                if (this.currentQuestKey === `achievement:${key}`)
                    this.buildLog(this.player.quests.tutorial);
            }
            return;
        }

        if (existing) {
            let p = existing.querySelector('p')!;
            p.innerHTML = this.formatAchievementName(task);

            if (this.currentQuestKey === `achievement:${key}`) this.buildLog(task, key);
            return;
        }

        let element = document.createElement('li'),
            name = document.createElement('p');

        element.classList.add('container-slot');
        name.classList.add('stroke', 'yellow');
        name.innerHTML = this.formatAchievementName(task);

        element.append(name);
        this.list.append(element);
        element.addEventListener('click', () => this.buildLog(task, key));

        this.achievementElements.set(key, element);
    }

    private formatAchievementName(task: Task): string {
        let progress = task.stageCount > 1 ? ` (${task.stage - 1}/${task.stageCount - 1})` : '';
        return `🎯 ${task.name}${progress}`;
    }

    private handleProgress(key: string): void {
        if (!key) return;

        let quest = this.player.quests[key];
        if (!quest) return;

        let element = this.list.children[quest.id],
            nameElement = element.querySelector('p')!;

        if (!nameElement) return;

        nameElement.classList.remove('green', 'yellow');

        if (quest.isFinished()) nameElement.classList.add('green');
        else if (quest.isStarted()) nameElement.classList.add('yellow');

        // Refresh log if this quest is currently displayed
        if (this.currentQuestKey === key) this.buildLog(quest);
    }

    private createElement(quest: Task): void {
        let element = document.createElement('li'),
            name = document.createElement('p');

        element.classList.add('container-slot');
        name.classList.add('stroke');
        name.innerHTML = quest.name;

        if (quest.isFinished()) name.classList.add('green');
        else if (quest.isStarted()) name.classList.add('yellow');

        element.append(name);
        this.list.append(element);
        element.addEventListener('click', () => this.buildLog(quest));
    }

    private buildLog(quest: Task, achievementKey?: string): void {
        let [shortDescription, description] = quest.description.split('|');

        if (achievementKey) this.currentQuestKey = `achievement:${achievementKey}`;
        else
            this.currentQuestKey =
                quest.id === undefined
                    ? ''
                    : Object.keys(this.player.quests).find(
                          (k) => this.player.quests[k] === quest
                      ) ?? '';

        this.title.innerHTML = quest.name;
        this.shortDescription.innerHTML = shortDescription;
        this.description.innerHTML = description || '';

        this.rewards.innerHTML = quest.rewards ? quest.rewards.join('<br>') : '';

        if (achievementKey && quest.stageCount > 1 && !quest.isFinished()) {
            this.killTracker.hidden = false;
            this.renderAchievementProgress(quest);
        } else if (!achievementKey && quest.killTask && !quest.isFinished()) {
            this.killTracker.hidden = false;
            this.renderKillTracker(quest);
        } else if (!achievementKey && quest.itemTask && !quest.isFinished()) {
            this.killTracker.hidden = false;
            this.renderItemTracker(quest);
        } else this.killTracker.hidden = true;

        // Show flag section only for CTF quests that are not finished
        let showFlag = !achievementKey && quest.ctf && !quest.isFinished();
        this.flagSection.hidden = !showFlag;
        this.flagInput.value = '';
        this.flagMsg.textContent = '';
    }

    private renderAchievementProgress(task: Task): void {
        this.killList.innerHTML = '';

        let killed = task.stage - 1,
            required = task.stageCount - 1,
            remaining = Math.max(0, required - killed),
            done = remaining === 0,
            li = document.createElement('li');
        li.className = 'kill-entry';

        let nameSpan = document.createElement('span');
        nameSpan.className = 'kill-name stroke';
        nameSpan.textContent = 'Progreso';

        let countSpan = document.createElement('span');
        countSpan.className = done ? 'kill-done' : 'kill-count';
        countSpan.textContent = done ? '✓' : `${killed}/${required}`;

        li.append(nameSpan, countSpan);
        this.killList.append(li);
    }

    private renderKillTracker(quest: Task): void {
        this.killList.innerHTML = '';
        if (!quest.killTask) return;

        for (let obj of quest.killTask.objectives) {
            let remaining = Math.max(0, obj.required - obj.killed),
                done = remaining === 0,
                li = document.createElement('li');
            li.className = 'kill-entry';

            let nameSpan = document.createElement('span');
            nameSpan.className = 'kill-name stroke';
            nameSpan.textContent = obj.name;

            let countSpan = document.createElement('span');
            countSpan.className = done ? 'kill-done' : 'kill-count';
            countSpan.textContent = done ? '✓' : `x${remaining}`;

            li.append(nameSpan, countSpan);
            this.killList.append(li);
        }
    }

    private renderItemTracker(quest: Task): void {
        this.killList.innerHTML = '';
        if (!quest.itemTask) return;

        for (let obj of quest.itemTask.objectives) {
            let done = obj.collected >= obj.required,
                li = document.createElement('li');
            li.className = 'kill-entry';

            let nameSpan = document.createElement('span');
            nameSpan.className = 'kill-name stroke';
            nameSpan.textContent = obj.name;

            let countSpan = document.createElement('span');
            countSpan.className = done ? 'kill-done' : 'kill-count';
            countSpan.textContent = done ? '✓' : `${obj.collected}/${obj.required}`;

            li.append(nameSpan, countSpan);
            this.killList.append(li);
        }
    }

    private submitFlag(): void {
        let flag = this.flagInput.value.trim();
        if (!flag || !this.currentQuestKey) return;

        this.flagMsg.textContent = '';
        this.flagSubmitCallback?.(this.currentQuestKey, flag);
    }

    public onFlagSubmit(callback: FlagSubmitCallback): void {
        this.flagSubmitCallback = callback;
    }
}
